import type { QuoteDetailsSummary } from '@/lib/api/quote-details'
import { getStoredSubtotalAdjustment } from '@/lib/proposal-display'
import type { QuoteData } from '@/lib/api/quotes'
import { QuoteStateCode, QuoteStatusCode, QuoteType } from '@/lib/types'
import { getMarginPercentage } from '@/lib/margin'
import { Decimal } from 'decimal.js'

/** Activation freezes header aggregates. Temporarily parked COs retain those
 * amounts while the worker rebases their rows; editable Drafts track live math. */
export function shouldUseStoredTotals(quote: QuoteData | null | undefined): boolean {
  return quote != null && (quote.statecode !== QuoteStateCode.Draft || quote.statuscode === QuoteStatusCode.Draft_Rebasing)
}

/** Overlay the quote's frozen header aggregates onto the live summary.
 *
 * Activation validates and freezes totallineitemamount/totaldiscountamount/
 * totaltax/totalamount; from then on they are the signed contract and must
 * display verbatim. The live recompute can drift by cents from the frozen
 * header (post-lock line edits, rounding changes), which showed on locked
 * imported proposals. Callers decide WHEN this applies (non-Draft quotes,
 * unfiltered summaries); this only does the arithmetic. */
export function applyStoredTotals({ quote, summary }: { quote: QuoteData; summary: QuoteDetailsSummary }): QuoteDetailsSummary {

  const subtotal = new Decimal(quote.totallineitemamount ?? summary.subtotalWithSuppliesAndDiscounts).toDecimalPlaces(2)
  const totalDiscount = new Decimal(quote.totaldiscountamount ?? summary.discounts.total).abs().toDecimalPlaces(2)
  const storedSubtotalAdjustment = getStoredSubtotalAdjustment(
    subtotal.toNumber(),
    new Decimal(summary.subtotalWithSupplies).sub(totalDiscount).toNumber()
  )
  // Activation freezes the header aggregates; from then on totaltax and
  // totalamount are the signed contract and must display verbatim. The live
  // tax recompute can drift by cents from the frozen header (post-lock line
  // edits, rounding changes), which showed on locked imported proposals.
  const tax = new Decimal(quote.totaltax ?? summary.tax.price).toDecimalPlaces(2)
  const total = new Decimal(quote.totalamount ?? subtotal.plus(tax)).toDecimalPlaces(2)
  const totalCost = new Decimal(quote.msdyn_estimatedcost ?? summary.totalCost).toDecimalPlaces(2)
  const laborHours = new Decimal(quote.dsy_totalhours ?? summary.labor.hours).toDecimalPlaces(5)
  const suppliesPrice = new Decimal(summary.supplies.price).toDecimalPlaces(2)
  const suppliesCost = new Decimal(summary.supplies.cost).toDecimalPlaces(2)
  const suppliesTax = new Decimal(summary.supplies.tax).toDecimalPlaces(2)
  const productPriceWithDiscounts = new Decimal(summary.product.priceWithDiscounts).toDecimalPlaces(2)
  const productCost = new Decimal(summary.product.cost).toDecimalPlaces(2)
  const laborPriceWithDiscounts = subtotal.sub(productPriceWithDiscounts).sub(suppliesPrice).toDecimalPlaces(2)

  if (laborPriceWithDiscounts.isNegative()) return summary

  const laborPrice = laborPriceWithDiscounts.plus(summary.discounts.labor).toDecimalPlaces(2)
  const laborCost = totalCost.sub(productCost).sub(suppliesCost).toDecimalPlaces(2)
  const laborTax = new Decimal(summary.labor.tax).toDecimalPlaces(2)
  const productTax = tax.sub(laborTax).sub(suppliesTax).toDecimalPlaces(2)
  const grossProfitPerHour = laborHours.isZero() ? new Decimal(0) : subtotal.sub(totalCost).div(laborHours)

  return {
    ...summary,
    product: {
      ...summary.product,
      tax: productTax.toNumber(),
      subtotalPercentage: subtotal.isZero()
        ? 0
        : productPriceWithDiscounts.div(subtotal).mul(100).toDecimalPlaces(2).toNumber(),
    },
    labor: {
      ...summary.labor,
      hours: laborHours.toNumber(),
      price: laborPrice.toNumber(),
      priceWithDiscounts: laborPriceWithDiscounts.toNumber(),
      cost: laborCost.toNumber(),
      margin: getMarginPercentage(laborPriceWithDiscounts, laborCost).toNumber(),
      subtotalPercentage: subtotal.isZero()
        ? 0
        : laborPriceWithDiscounts.div(subtotal).mul(100).toDecimalPlaces(2).toNumber(),
    },
    supplies: {
      ...summary.supplies,
      subtotalPercentage: subtotal.isZero() ? 0 : suppliesPrice.div(subtotal).mul(100).toDecimalPlaces(2).toNumber(),
    },
    tax: {
      ...summary.tax,
      price: tax.toNumber(),
    },
    discounts: {
      ...summary.discounts,
      total: totalDiscount.toNumber(),
    },
    subtotalWithSuppliesAndDiscounts: subtotal.toNumber(),
    storedSubtotalAdjustment,
    total: total.toNumber(),
    totalCost: totalCost.toNumber(),
    totalMargin: getMarginPercentage(subtotal, totalCost).toNumber(),
    grossProfitPerHour: grossProfitPerHour.toDecimalPlaces(2).toNumber(),
  }
}

/** CO headers contain the frozen delta, never the cumulative current quote.
 * Preserve gross categories and the delta's signed discount: Dataverse stores
 * totaldiscountamount as an absolute magnitude even when a CO removes a discount. */
export function applyStoredChangeOrderTotals({ quote, summary }: {
  quote: QuoteData
  summary: QuoteDetailsSummary
}): QuoteDetailsSummary {
  if (quote.dsy_type !== QuoteType.ChangeOrder || !shouldUseStoredTotals(quote)) return summary
  const subtotal = new Decimal(quote.totallineitemamount ?? summary.subtotalWithSuppliesAndDiscounts).toDecimalPlaces(2)
  const discountMagnitude = new Decimal(quote.totaldiscountamount ?? summary.discounts.total).abs()
  const discount = discountMagnitude.mul(summary.discounts.total < 0 ? -1 : 1).toDecimalPlaces(2)
  const tax = new Decimal(quote.totaltax ?? summary.tax.price).toDecimalPlaces(2)
  const totalCost = new Decimal(quote.msdyn_estimatedcost ?? summary.totalCost).toDecimalPlaces(2)
  const hours = new Decimal(quote.dsy_totalhours ?? summary.labor.hours).toDecimalPlaces(5)
  return {
    ...summary,
    labor: { ...summary.labor, hours: hours.toNumber() },
    discounts: { ...summary.discounts, total: discount.toNumber() },
    tax: { ...summary.tax, price: tax.toNumber() },
    subtotalWithSuppliesAndDiscounts: subtotal.toNumber(),
    storedSubtotalAdjustment: getStoredSubtotalAdjustment(
      subtotal.toNumber(),
      new Decimal(summary.subtotalWithSupplies).sub(discount).toNumber()
    ),
    total: new Decimal(quote.totalamount ?? subtotal.plus(tax)).toDecimalPlaces(2).toNumber(),
    totalCost: totalCost.toNumber(),
  }
}
